<template>
    <div>
        <div class="d-flex justify-content-between">
            <h1 class="page-header">
                {{ $t('eventManagement.pageTitle') }}
            </h1>
            <ts-breadcrumb :items="[
                { text: $t('toHomePage'), href: '/' },
                { text: $t('eventManagement.pageTitle'), active: true }
            ]" />
        </div>

        <div>
            <ts-card>
                <ts-card-wrapper>
                    <div class="d-flex justify-content-between">
                        <ts-button color="info" outline @click="showForm()">
                            {{ $t('eventManagement.add_new') }}
                        </ts-button>
                        <ts-input v-model="search" :placeholder="$t('eventManagement.search')" style="width: 300px;" />
                    </div>
                </ts-card-wrapper>

                <div class="overflow-auto">
                    <ts-table :columns="columns" :records="resources" :loading="loading">
                        <template v-slot="{ record }">
                            <td class="ts-whitespace-no-wrap">
                                {{ record.event_name }}
                            </td>
                            <td class="ts-whitespace-no-wrap">
                                {{ record.event_location }}
                            </td>
                            <td class="ts-whitespace-no-wrap">
                                {{ record.event_start_date }}
                            </td>
                            <td class="ts-whitespace-no-wrap">
                                {{ record.event_end_date }}
                            </td>
                            <td class="ts-whitespace-no-wrap">
                                {{ record.event_description }}
                            </td>
                            <td class="ts-whitespace-no-wrap text-center">
                                <a href="#" class="mr-2 text-primary"
                                    v-tooltip="$t('school.academic.operation.classOperation.student')"
                                    @click.prevent="showStudentList(record)">
                                    <i class="fas fa-eye"></i>
                                </a>

                                <a href="#" v-tooltip="$t('qrGenerate')" class="mr-2" style="color: #17a2b8;"
                                    @click.prevent="onGenerateQr(record)">
                                    <i class="fas fa-qrcode"></i>
                                </a>
                                <a href="#" v-tooltip="$t('edit')" class="text-primary mr-2"
                                    @click.prevent="onEdit(record)">
                                    <i class="fas fa-edit"></i>
                                </a>
                                <a href="#" v-tooltip="$t('delete')" class="text-danger"
                                    @click.prevent="destroy(record)" :disabled="record._deleting">
                                    <i class="fas fa-circle-notch spin" v-if="record._deleting"></i>
                                    <i class="fas fa-trash" v-else></i>
                                </a>
                            </td>
                        </template>
                    </ts-table>
                </div>

                <div class="d-flex justify-content-end p-15">
                    <ts-pagination v-model="pagination" @navigate="fetchData" />
                </div>
            </ts-card>
        </div>

        <!--  MODAL SECTION -->
        <a-modal v-if="show_form" v-model="show_form" :title="$t('eventManagement.pageTitle')" :zIndex="1020"
            :footer="null" :maskClosable="false" width="60%" @cancel="clearEdit">
            <form-component ref="form_component" :editData="editData" @close="closeModal" />
        </a-modal>
        <a-modal v-model="show_student_list" centered @ok="() => (show_student_list = false)" :zIndex="1020"
            :cancelText="$t('cancel')" :okText="$t('ok')" :footer="null" width="50%">
            <template v-slot:title>
                <div class="ts-flex ts-justify-between ts-items-baseline">
                    <div>
                        <h2>
                            {{ $t('eventManagement.eventRegistrationLIst') }}
                        </h2>
                    </div>

                </div>
            </template>
        </a-modal>
    </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FormComponent from './form.vue'
import PreviewQr from './components/preview-qr.vue'
import StudentList from './components/view-student-list.vue'

export default {
    name: 'eventManagement',
    components: { FormComponent, StudentList },
    data() {
        return {
            loading: false,
            show_form: false,
            editData: null,
            show_student_list: false,
            registrations: []
        }
    },
    created() {
        this.fetchData()
    },
    computed: {
        ...mapState('eventManagement', ['resources', 'pagination']),
        search: {
            get() {
                return this.$store.state.eventManagement.search
            },
            set(newValue) {
                this.$store.commit('eventManagement/SET_SEARCH', newValue)
                this.$store.commit('eventManagement/RESET_CURRENT_PAGE')
            }
        },
        columns() {
            return [
                { name: this.$t('eventManagement.eventName') },
                { name: this.$t('eventManagement.eventLocation') },
                { name: this.$t('eventManagement.eventStartDate') },
                { name: this.$t('eventManagement.eventEndDate') },
                { name: this.$t('eventManagement.eventDescription') },
                {
                    name: this.$t('eventManagement.actions'),
                    style: 'text-align:center; width:1px;'
                }
            ]
        }
    },
    methods: {
        fetchData(attributes) {
            this.loading = true
            this.$store
                .dispatch('eventManagement/fetch', attributes)
                .then(() => (this.loading = false))
                .catch(error => {
                    this.$toasted.error(error.message)
                    this.loading = false
                })
        },
        showForm(editData = null) {
            this.editData = editData
            this.show_form = true
        },
        onEdit(record) {
            this.showForm(record)
        },
        closeModal() {
            this.show_form = false
            this.fetchData()
        },
        clearEdit() {
            this.editData = null
            this.show_form = false
        },
        async destroy(record) {
            if (
                await this.$confirmModal.danger(
                    this.$t(
                        'school.academic.setup.program.subject.activity_subjects.are_you_sure_to_delete'
                    )
                )
            ) {
                record._deleting = true
                this.$store
                    .dispatch('eventManagement/destroy', record.event_mgt_id)
                    .then(response => {
                        this.fetchData()
                        this.$toasted.success(response.message)
                        record._deleting = false
                    })
                    .catch(error => {
                        this.$toasted.error(error.message)
                        record._deleting = false
                    })
            }
        },
        onGenerateQr(record) {
            this.$modal.show(
                PreviewQr,
                {
                    event_mgt_id: record.event_mgt_id,
                },
                {
                    height: 'auto',
                    width: '50%',
                    scrollable: true,
                    draggable: '.form-header',
                    clickToClose: false
                }
            )
        },
        showStudentList(record) {
            this.$store.commit('eventManagement/SET_EDIT_DATA', record)
            this.$router.push({
                name: 'event-management-show',
                params: { id: record.event_mgt_id }
            })
        }
    },
    watch: {
        search: debounce(function (search) {
            this.fetchData({ search })
        }, 500)
    },
    beforeRouteLeave(to, from, next) {
        if (_.isEmpty(to.params)) {
            this.$store.commit('eventManagement/RESET_STATE')
            this.$store.commit('eventManagement/SET_EDIT_DATA', {})
        }
        next()
    }
}
</script>
